import { replaceProperties } from '../../object.helpers';
import { getProposals } from '../../proposals/proposals.helpers';
import type { Baseline } from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import type { SemiPartial } from '@shared/models/type-helpers.model';

/**
 * Apply partial updates (e.g. rename, color, active) to one or more {@link IProposal} rows
 * in {@link Baseline.proposals}. Mirrors `updateProposals` for v1.
 *
 * Returns the same {@link version} reference when nothing changes; throws when any update
 * targets a missing proposal id (matches v1 invariant).
 */
export const updateProposals = (
  version: Baseline,
  ...updates: SemiPartial<IProposal, 'id'>[]
): Baseline => {
  if (updates.length === 0) return version;
  const current = getProposals(version);

  // Index updates by id once so the validation pass and the map body are O(n + m)
  // instead of O(n × m), and so the thrown error can include the missing id.
  const updatesById = new Map(updates.map((u) => [u.id, u] as const));
  const currentIds = new Set(current.map((p) => p.id));
  const missing = updates.find((u) => !currentIds.has(u.id));
  if (missing) {
    throw new Error(`Proposal not found: ${missing.id}`);
  }

  let mutated = false;
  const nextProposals = current.map((p) => {
    const update = updatesById.get(p.id);
    if (!update) return p;
    const next = replaceProperties(p, update);
    if (next !== p) mutated = true;
    return next;
  });

  if (!mutated) return version;
  return { ...version, proposals: nextProposals };
};
